/* ============================================================
   AdminPanel — TASK-178: user/role management + platform
   operational settings. Mounted only for Admin role sessions.
   Light theme, iOS-minimal (DESIGN-SYSTEM.md).
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import type { AdminTab } from '../routing';
import { ROLE_LABELS } from '../types/auth';
import { formatRub } from '../types/product';
import { normalizeOrderStatus } from '../types/product';
import type { WebOrderStatus } from '../types/product';
import type { AdminDispute, AdminDisputePhoto, AdminOrder, AdminUser, DisputeOutcome, PlatformSettings, ProviderCommission } from '../types/admin';
import { AdminCategoriesPanel } from './AdminCategoriesPanel';
import { AdminPaymentsPanel } from './AdminPaymentsPanel';
import { AdminReferralPanel } from './AdminReferralPanel';
import { commissionPercentToText, commissionRowLabel, parseCommissionPercent } from './adminSettingsView';
import { CueChip } from './CueChip';
import { ListPager } from './ListPager';
import { SlaBadge } from './SlaBadge';
import { orderColorModeRow, orderColorRow } from './orderColorView';
import { buildShippedTrackingView } from './shippedTrackingView';
import { buildDeliveryAddressStrip, deliveryAddressStripLabel, deliveryAddressStripValue } from './deliveryAddressStrip';

const ROLES = ['Customer', 'Provider', 'Manager', 'Admin'];

/* 423ee061: page size for the three admin list tabs (server-side paging). */
const ADMIN_PAGE_SIZE = 10;

/* 423ee061: admin filter controls (search input + selects + date range) —
 * light-theme iOS-minimal, same input treatment as the settings forms. */
const filterInputStyle: React.CSSProperties = {
  height: 36,
  padding: '0 10px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  fontSize: 'var(--fs-caption)',
  color: 'var(--color-text-primary)',
};

const filterRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-sm)',
  flexWrap: 'wrap',
  alignItems: 'center',
};

/* TASK-245: admin-side status presentation (same labels as customer OrdersPage). */
const ORDER_STATUS_LABELS: Record<WebOrderStatus, string> = {
  Pending: 'Ожидает',
  Accepted: 'Принят',
  Printing: 'Печатается',
  Printed: 'Ждёт подтверждения',
  Shipped: 'Отправлен',
  Completed: 'Завершён',
  Cancelled: 'Отменён',
  Disputed: 'Спор',
};

const ORDER_STATUS_BADGE: Record<WebOrderStatus, string> = {
  Pending: 'badge-warning',
  Accepted: 'badge-warning',
  Printing: 'badge-warning',
  Printed: 'badge-warning',
  Shipped: 'badge-success',
  Completed: 'badge-success',
  Cancelled: 'badge-danger',
  Disputed: 'badge-danger',
};

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}

/** TASK-245: dispute-age badge for triage — neutral <2 days, amber 2–5, red >5. */
function DisputeAgeBadge({ disputedAtUtc }: { disputedAtUtc: string | null }) {
  if (!disputedAtUtc) return null;
  const days = Math.max(0, Math.floor((Date.now() - new Date(disputedAtUtc).getTime()) / 86_400_000));
  const cls = days > 5 ? 'badge-danger' : days >= 2 ? 'badge-warning' : 'badge-neutral';
  return (
    <span
      className={cls}
      title={`Спор открыт с ${new Date(disputedAtUtc).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}`}
      style={{ fontSize: 'var(--fs-caption)' }}
    >
      спор {days} {pluralDays(days)}
    </span>
  );
}

const shortId = (id: string): string => id.slice(0, 8).toUpperCase();

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** TASK-262: kind-split photo tiles for the dispute card — same grouped +
 *  counted presentation as the customer detail and provider queue. */
function DisputePhotoTile({ photo, alt }: { photo: AdminDisputePhoto; alt: string }) {
  return (
    <a
      href={resolveMediaUrl(photo.url)}
      target="_blank"
      rel="noreferrer"
      title={photo.caption ?? alt}
    >
      <img
        src={resolveMediaUrl(photo.url)}
        alt={photo.caption ?? alt}
        loading="lazy"
        style={{
          width: 96,
          height: 96,
          objectFit: 'cover',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-separator)',
          background: 'var(--color-bg)',
        }}
      />
    </a>
  );
}

/* 31c9ace7: admin tracking row — carrier-labeled number + «Отследить» deep
 * link. Same pure shippedTrackingView as the customer/provider surfaces
 * (checkout carrier snapshot wins over the number-shape guess), so support
 * quotes exactly what the customer sees. */
function AdminTrackingRow({ trackingNumber, deliveryCarrier }: { trackingNumber: string; deliveryCarrier?: string | null }) {
  const view = buildShippedTrackingView({ trackingNumber, deliveryCarrier });
  const labeled = view.carrierLabel != null
    ? `${view.trackingNumber} · ${view.carrierLabel}`
    : (view.trackingNumber ?? trackingNumber);
  return (
    <div>
      <strong>Трек-номер:</strong> {labeled}
      {view.trackUrl != null && (
        <> · <a href={view.trackUrl} target="_blank" rel="noreferrer">Отследить</a></>
      )}
    </div>
  );
}

export function AdminPanel({ initialTab = 'users' }: { initialTab?: AdminTab } = {}) {
  const [tab, setTab] = useState<AdminTab>(initialTab);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userOffset, setUserOffset] = useState(0);
  const [userQuery, setUserQuery] = useState('');
  const [userRole, setUserRole] = useState('');  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [disputes, setDisputes] = useState<AdminDispute[]>([]);
  const [disputesTotal, setDisputesTotal] = useState(0);
  const [disputeOffset, setDisputeOffset] = useState(0);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [orderOffset, setOrderOffset] = useState(0);
  const [orderStatus, setOrderStatus] = useState('');
  const [orderMethod, setOrderMethod] = useState('');
  const [orderFrom, setOrderFrom] = useState('');
  const [orderTo, setOrderTo] = useState('');
  const [orderQuery, setOrderQuery] = useState('');
  /* 802d765a: cabinet sort controls — default createdAt/desc keeps «сначала
   * новые» (425131a5: the Sep-1 order hid behind Aug-29 pages). */
  const [orderSort, setOrderSort] = useState<'createdAt' | 'status' | 'total'>('createdAt');
  const [orderDir, setOrderDir] = useState<'desc' | 'asc'>('desc');
  const [userQueryDraft, setUserQueryDraft] = useState('');
  const [orderQueryDraft, setOrderQueryDraft] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /* 1c936a07: marketplace commission hub — global default draft (string so
   * blank = null stays representable) + per-provider override editor. */
  const [commissionText, setCommissionText] = useState('');
  const [providers, setProviders] = useState<ProviderCommission[]>([]);
  const [providerDrafts, setProviderDrafts] = useState<Record<string, string>>({});
  const [savingProviderId, setSavingProviderId] = useState<string | null>(null);

  /* 423ee061: all three list tabs load one server-side page (filters +
   * offset live here; changing a filter resets to the first page). */
  const load = useCallback(async () => {
    setError(null);
    try {
      if (tab === 'users') {
        const page = await api.getAdminUsersPage(userQuery.trim(), userRole, userOffset, ADMIN_PAGE_SIZE);
        setUsers(page.items);
        setUsersTotal(page.total);
      } else if (tab === 'orders') {
        const page = await api.getAdminOrdersPage({
          status: orderStatus,
          deliveryMethod: orderMethod,
          fromUtc: orderFrom ? `${orderFrom}T00:00:00Z` : undefined,
          toUtc: orderTo ? `${orderTo}T23:59:59Z` : undefined,
          q: orderQuery.trim(),
          sort: orderSort,
          dir: orderDir,
          offset: orderOffset,
          limit: ADMIN_PAGE_SIZE,
        });
        setOrders(page.items);
        setOrdersTotal(page.total);
      } else if (tab === 'settings') {
        setSettings(await api.getAdminSettings());
        // 1c936a07: provider override rows live next to the global default.
        setProviders(await api.getProviderCommissions());
      } else if (tab === 'disputes') {
        const page = await api.getAdminDisputesPage(disputeOffset, ADMIN_PAGE_SIZE);
        setDisputes(page.items);
        setDisputesTotal(page.total);
      }
      // 'categories' tab owns its data inside AdminCategoriesPanel
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить данные');
    }
  }, [tab, userQuery, userRole, userOffset, orderStatus, orderMethod, orderFrom, orderTo, orderQuery, orderSort, orderDir, orderOffset, disputeOffset]);

  useEffect(() => { void load(); }, [load]);

  /* 1c936a07: the global default draft mirrors the loaded settings row
   * (blank = not overridden). Kept in sync on load; typed edits are local. */
  useEffect(() => {
    setCommissionText(commissionPercentToText(settings?.marketplaceCommissionPercent ?? null));
    setProviderDrafts({});
  }, [settings?.updatedAtUtc]);

  const changeRole = async (user: AdminUser, role: string) => {
    setError(null);
    setNotice(null);
    try {
      await api.setAdminUserRole(user.id, role);
      setNotice(`Роль «${user.displayName}» → ${ROLE_LABELS[role] ?? role}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить роль');
    }
  };

  const saveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    setError(null);
    setNotice(null);
    // 1c936a07: the global commission default is edited as text (blank =
    // follow config); validate here, mirroring the backend band (0; 50].
    const parsed = parseCommissionPercent(commissionText);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    try {
      setSettings(await api.saveAdminSettings({
        ...settings,
        marketplaceCommissionPercent: parsed.value,
      }));
      setNotice('Настройки сохранены');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить настройки');
    }
  };

  // AS-1 (TASK-6e4bd7b5): the payments section saves the whole settings
  // row (platform fields + Safe Deal overrides) in one PUT — same contract
  // as the platform form above, single source of truth in `settings` state.
  /* 1c936a07: save (or clear) one provider's commission override. Applies to
   * future fee resolutions only — past orders keep their snapshots. */
  const saveProviderCommission = async (provider: ProviderCommission, raw: string) => {
    setError(null);
    setNotice(null);
    const parsed = parseCommissionPercent(raw);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setSavingProviderId(provider.userId);
    try {
      const saved = await api.setProviderCommissionRate(provider.userId, parsed.value);
      setProviders((prev) => prev.map((p) => (p.userId === saved.userId ? saved : p)));
      setProviderDrafts((prev) => {
        const next = { ...prev };
        delete next[provider.userId];
        return next;
      });
      setNotice(`Комиссия «${provider.companyName}» ${saved.commissionRatePercent != null ? `→ ${saved.commissionRatePercent} %` : 'сброшена'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить комиссию');
    } finally {
      setSavingProviderId(null);
    }
  };
  const savePaymentsSettings = async (next: PlatformSettings) => {
    setError(null);
    setNotice(null);
    try {
      setSettings(await api.saveAdminSettings(next));
      setNotice('Платежные настройки сохранены');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить платежные настройки');
    }
  };

  // ── TASK-242: dispute resolution ───────────────────

  const resolveDispute = async (dispute: AdminDispute, outcome: DisputeOutcome) => {
    const actionLabel = outcome === 'Completed' ? 'завершить' : 'отменить';
    if (!window.confirm(`Действительно ${actionLabel} заказ №${shortId(dispute.id)}? Действие необратимо.`)) return;
    setError(null);
    setNotice(null);
    setBusyId(dispute.id);
    try {
      await api.resolveAdminDispute(dispute.id, outcome, notes[dispute.id] || undefined);
      setNotice(`Спор по заказу №${shortId(dispute.id)} разрешён (${outcome === 'Completed' ? 'завершён' : 'отменён'})`);
      setNotes((prev) => {
        const next = { ...prev };
        delete next[dispute.id];
        return next;
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось разрешить спор');
    } finally {
      setBusyId(null);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-separator)',
    borderRadius: 'var(--radius-lg)',
    padding: '24px',
    maxWidth: '640px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: 'var(--fs-caption)',
    fontWeight: 'var(--fw-semibold)',
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--color-separator)',
    borderRadius: '12px',
    padding: '10px 12px',
    fontSize: 'var(--fs-body)',
    fontWeight: 'var(--fw-regular)',
    color: 'var(--color-text-primary)',
    background: 'var(--color-surface)',
  };

  return (
    <section style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'var(--fw-bold)', margin: 0 }}>Админ-панель</h1>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {(['users', 'categories', 'orders', 'disputes', 'settings'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                border: '1px solid var(--color-separator)',
                borderRadius: '999px',
                padding: '7px 14px',
                fontSize: 'var(--fs-caption)',
                fontWeight: 'var(--fw-semibold)',
                cursor: 'pointer',
                background: tab === t ? 'var(--color-primary)' : 'var(--color-surface)',
                color: tab === t ? '#fff' : 'var(--color-text-primary)',
              }}
            >
              {t === 'users' ? 'Пользователи' : t === 'categories' ? 'Категории' : t === 'orders' ? 'Заказы' : t === 'disputes' ? `Споры${disputesTotal > 0 ? ` (${disputesTotal})` : ''}` : 'Настройки платформы'}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div style={{ background: 'rgba(255,59,48,0.08)', color: '#FF3B30', borderRadius: '12px', padding: '10px 14px', fontSize: 'var(--fs-body)' }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ background: 'rgba(52,199,89,0.1)', color: '#248A3D', borderRadius: '12px', padding: '10px 14px', fontSize: 'var(--fs-body)' }}>
          {notice}
        </div>
      )}

      {tab === 'users' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          {/* 423ee061: server-side email/name search + role filter */}
          <form
            style={filterRowStyle}
            onSubmit={(e) => {
              e.preventDefault();
              setUserOffset(0);
              setUserQuery(userQueryDraft);
            }}
          >
            <input
              style={{ ...filterInputStyle, flex: '1 1 220px' }}
              placeholder="Поиск по email или имени…"
              value={userQueryDraft}
              onChange={(e) => setUserQueryDraft(e.target.value)}
            />
            <select
              style={filterInputStyle}
              value={userRole}
              onChange={(e) => {
                setUserOffset(0);
                setUserRole(e.target.value);
              }}
            >
              <option value="">Все роли</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
              ))}
            </select>
            <button className="btn btn-secondary" style={{ height: 36, padding: '0 16px', fontSize: 'var(--fs-caption)' }}>
              Найти
            </button>
          </form>

          <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-body)' }}>
              <thead>
                <tr>
                  {['Имя', 'Email', 'Город', 'Баланс', 'Роль'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid var(--color-separator)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-separator)' }}>{u.displayName}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-separator)' }}>{u.email}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-separator)' }}>{u.city || '—'}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-separator)' }}>{u.balance.toLocaleString('ru-RU')} ₽</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-separator)' }}>
                      <select
                        value={u.role}
                        onChange={(e) => void changeRole(u, e.target.value)}
                        style={{ ...inputStyle, padding: '6px 10px' }}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>Пользователи не найдены</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <ListPager
            total={usersTotal}
            offset={userOffset}
            limit={ADMIN_PAGE_SIZE}
            onPrev={() => setUserOffset(Math.max(0, userOffset - ADMIN_PAGE_SIZE))}
            onNext={() => setUserOffset(userOffset + ADMIN_PAGE_SIZE)}
          />
        </div>
      )}

      {tab === 'categories' && <AdminCategoriesPanel />}

      {tab === 'orders' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* 423ee061: server-side status/date-range/counterparty filters */}
          <form
            style={filterRowStyle}
            onSubmit={(e) => {
              e.preventDefault();
              setOrderOffset(0);
              setOrderQuery(orderQueryDraft);
            }}
          >
            <input
              style={{ ...filterInputStyle, flex: '1 1 220px' }}
              placeholder="№ заказа, имя или email…"
              value={orderQueryDraft}
              onChange={(e) => setOrderQueryDraft(e.target.value)}
            />
            <select
              style={filterInputStyle}
              value={orderStatus}
              onChange={(e) => {
                setOrderOffset(0);
                setOrderStatus(e.target.value);
              }}
            >
              <option value="">Все статусы</option>
              {Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {/* 802d765a: delivery-method filter (самовывоз / ПВЗ / курьер). */}
            <select
              style={filterInputStyle}
              value={orderMethod}
              onChange={(e) => {
                setOrderOffset(0);
                setOrderMethod(e.target.value);
              }}
              aria-label="Способ доставки"
            >
              <option value="">Все способы</option>
              <option value="Pickup">Самовывоз</option>
              <option value="DropoffPoint">Пункт выдачи</option>
              <option value="Courier">Курьер</option>
            </select>
            <input type="date" style={filterInputStyle} value={orderFrom}
                   onChange={(e) => { setOrderOffset(0); setOrderFrom(e.target.value); }}
                   aria-label="Создан с" />
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>—</span>
            <input type="date" style={filterInputStyle} value={orderTo}
                   onChange={(e) => { setOrderOffset(0); setOrderTo(e.target.value); }}
                   aria-label="Создан до" />
            {/* 802d765a: sort key + direction (default дата ↓ = сначала новые). */}
            <select
              style={filterInputStyle}
              value={orderSort}
              onChange={(e) => {
                setOrderOffset(0);
                setOrderSort(e.target.value as 'createdAt' | 'status' | 'total');
              }}
              aria-label="Сортировка"
            >
              <option value="createdAt">Дата</option>
              <option value="status">Статус</option>
              <option value="total">Сумма</option>
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ height: 36, padding: '0 12px', fontSize: 'var(--fs-caption)' }}
              title={orderDir === 'desc' ? 'По убыванию' : 'По возрастанию'}
              onClick={() => {
                setOrderOffset(0);
                setOrderDir(orderDir === 'desc' ? 'asc' : 'desc');
              }}
            >
              {orderDir === 'desc' ? '↓' : '↑'}
            </button>
            <button className="btn btn-secondary" style={{ height: 36, padding: '0 16px', fontSize: 'var(--fs-caption)' }}>
              Найти
            </button>
          </form>

          {orders.length === 0 && (
            <div style={cardStyle}>Заказы не найдены</div>
          )}
          {orders.map((o) => {
            const status = normalizeOrderStatus(o.status);
            /* TASK-e46c5576: confirmed Printed flips to «Ждёт отправки» (whose
               court the ball is in, TASK-258) — same rule as the provider
               StatusBadge and customer OrdersPage; the ✓ chip follows the
               handoff through Shipped (history parity, provider rule). */
            const confirmed = o.customerConfirmedAtUtc != null;
            const confirmedCue = confirmed && (status === 'Printed' || status === 'Shipped');
            /* ADM-1 (AUDIT-8 GAP-B): stored color wherever it was snapshotted
               — quote AND cart legs (MS-2 stamps Order.PrintColor); the pure
               row helper keeps product-only/legacy rows hidden (null/blank). */
            const adminOrderColor = orderColorRow(o.printColor) ?? orderColorModeRow(o.printColor, o.printColorMode);
            /* c3e7f6d0 (Artem @b8810bf1): the same delivery address strip as
               the provider card — самовывоз → точка, курьер продавца → адрес
               клиента, ПВЗ/почта → только название службы. */
            const addressStrip = buildDeliveryAddressStrip({
              deliveryMethod: o.deliveryMethod,
              shippingAddress: o.shippingAddress,
              pickupPointWorkingHours: o.pickupPointWorkingHours,
              deliveryCarrier: o.deliveryCarrier,
            });
            return (
              <article key={o.id} style={{ ...cardStyle, maxWidth: '760px', gap: '10px' }}>
                <header style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' }}>
                  <h2 style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-bold)', margin: 0, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    Заказ №{shortId(o.id)} · {formatRub(o.totalPrice)}
                    <span className={ORDER_STATUS_BADGE[status]}>
                      {status === 'Printed' && confirmed ? 'Ждёт отправки' : ORDER_STATUS_LABELS[status]}
                    </span>
                    {confirmedCue && <CueChip label="✓ Подтверждён клиентом" tone="success" />}
                    {status === 'Disputed' && <DisputeAgeBadge disputedAtUtc={o.disputedAtUtc} />}
                    <SlaBadge sla={o.sla} deliveryMethod={o.deliveryMethod} />
                  </h2>
                  <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                    создан {fmtDate(o.createdAtUtc)}
                    {o.disputedAtUtc ? ' · спор с ' + fmtDate(o.disputedAtUtc) : ''}
                  </span>
                </header>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 16px', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  <div><strong>Заказчик:</strong> {o.customerName ?? '—'}{o.customerEmail ? ` (${o.customerEmail})` : ''}</div>
                  <div><strong>Исполнитель:</strong> {o.providerName ? `${o.providerName}${o.providerEmail ? ` (${o.providerEmail})` : ''}` : 'не назначен'}</div>
                  {adminOrderColor && <div><strong>Цвет:</strong> {adminOrderColor.value}</div>}
                  {addressStrip && <div><strong>{deliveryAddressStripLabel(addressStrip)}:</strong> {deliveryAddressStripValue(addressStrip)}</div>}
                  {o.trackingNumber && <AdminTrackingRow trackingNumber={o.trackingNumber} deliveryCarrier={o.deliveryCarrier} />}
                  {o.estimatedDeliveryUtc && <div><strong>Ожидаемая доставка:</strong> {fmtDate(o.estimatedDeliveryUtc)}</div>}
                </div>
              </article>
            );
          })}

          <ListPager
            total={ordersTotal}
            offset={orderOffset}
            limit={ADMIN_PAGE_SIZE}
            onPrev={() => setOrderOffset(Math.max(0, orderOffset - ADMIN_PAGE_SIZE))}
            onNext={() => setOrderOffset(orderOffset + ADMIN_PAGE_SIZE)}
          />
        </div>
      )}

      {tab === 'disputes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {disputes.length === 0 && (
            <div style={cardStyle}>Открытых споров нет</div>
          )}
          {disputes.map((d) => {
            /* ADM-1 (AUDIT-8 GAP-B): color visible where the dispute is
               decided — same snapshot-driven guard as the orders tab
               (product-only/legacy null stays hidden). */
            const disputeColor = orderColorRow(d.printColor);
            return (
            <article key={d.id} style={{ ...cardStyle, maxWidth: '760px' }}>
              <header style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' }}>
                <h2 style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-bold)', margin: 0, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  Заказ №{shortId(d.id)} · {formatRub(d.totalPrice)}
                  <DisputeAgeBadge disputedAtUtc={d.disputedAtUtc} />
                </h2>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  создан {fmtDate(d.createdAtUtc)} · спор с {fmtDate(d.disputedAtUtc)}
                </span>
              </header>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 16px', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                <div><strong>Заказчик:</strong> {d.customerName ?? '—'}{d.customerEmail ? ` (${d.customerEmail})` : ''}</div>
                <div><strong>Исполнитель:</strong> {d.providerName ? `${d.providerName}${d.providerEmail ? ` (${d.providerEmail})` : ''}` : 'не назначен'}</div>
                {disputeColor && <div><strong>Цвет:</strong> {disputeColor.value}</div>}
                {d.trackingNumber && <AdminTrackingRow trackingNumber={d.trackingNumber} deliveryCarrier={d.deliveryCarrier} />}
                {d.estimatedDeliveryUtc && <div><strong>Ожидаемая доставка:</strong> {fmtDate(d.estimatedDeliveryUtc)}</div>}
                {d.customerConfirmedAtUtc && <div><strong>Подтверждён клиентом:</strong> {fmtDate(d.customerConfirmedAtUtc)}</div>}
                {d.shippingAddress && <div style={{ gridColumn: '1 / -1' }}><strong>Адрес:</strong> {d.shippingAddress}</div>}
              </div>

              {/* TASK-262: same scannable status cues as the customer/provider
                  surfaces — confirmation stamp + kind-split photo counts. */}
              {(d.customerConfirmedAtUtc || d.progressPhotoCount > 0 || d.resultPhotoCount > 0) && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {d.customerConfirmedAtUtc && <CueChip label="✓ Подтверждён клиентом" tone="success" />}
                  {d.progressPhotoCount > 0 && <CueChip label={`📷 ${d.progressPhotoCount} фото процесса`} tone="neutral" />}
                  {d.resultPhotoCount > 0 && <CueChip label={`🖼 ${d.resultPhotoCount} фото результата`} tone="neutral" />}
                </div>
              )}

              <div style={{ background: 'rgba(255,59,48,0.06)', border: '1px solid rgba(255,59,48,0.15)', borderRadius: '12px', padding: '10px 14px' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#C0392B', marginBottom: '4px' }}>Причина спора</div>
                <div style={{ fontSize: 'var(--fs-body)' }}>{d.disputeReason ?? '—'}</div>
              </div>

              {/* dc25865a (audit-12 §5 Card A): customer dispute evidence —
                  read-only thumbs like the chat surfaces; click opens full size. */}
              {(d.disputePhotos?.length ?? 0) > 0 && (
                <div>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>Фото доказательств клиента · {d.disputePhotos!.length}</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {d.disputePhotos!.map((url) => (
                      <a key={url} href={resolveMediaUrl(url)} target="_blank" rel="noreferrer">
                        <img
                          src={resolveMediaUrl(url)}
                          alt="Фото доказательств клиента"
                          loading="lazy"
                          style={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-separator)',
                            background: 'var(--color-bg)',
                        }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {d.resultNote && (
                <div>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Комментарий исполнителя к результату</div>
                  <div style={{ fontSize: 'var(--fs-body)' }}>{d.resultNote}</div>
                </div>
              )}

              {/* TASK-262: photo proof split by kind and counted — progress
                  updates never mix into the result-proof grid (TASK-250). */}
              {(() => {
                const progress = d.resultPhotos.filter((p) => p.kind === 1);
                const result = d.resultPhotos.filter((p) => p.kind !== 1);
                return (
                  <>
                    {progress.length > 0 && (
                      <div>
                        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>Фото процесса · {progress.length}</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {progress.map((p) => (<DisputePhotoTile key={p.photoId} photo={p} alt="Фото процесса" />))}
                        </div>
                      </div>
                    )}
                    {result.length > 0 && (
                      <div>
                        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>Фото результата · {result.length}</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {result.map((p) => (<DisputePhotoTile key={p.photoId} photo={p} alt="Фото результата" />))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              <label style={fieldStyle}>
                Комментарий к решению (необязательно)
                <input
                  type="text"
                  value={notes[d.id] ?? ''}
                  maxLength={2000}
                  placeholder="Например: спору не подтверждён — договорились о повторной печати"
                  onChange={(e) => setNotes((prev) => ({ ...prev, [d.id]: e.target.value }))}
                  style={inputStyle}
                />
              </label>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={busyId === d.id}
                  onClick={() => void resolveDispute(d, 'Completed')}
                  style={{ border: 'none', borderRadius: '999px', background: 'var(--color-primary)', color: '#fff', padding: '10px 20px', fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', cursor: busyId === d.id ? 'default' : 'pointer', opacity: busyId === d.id ? 0.6 : 1 }}
                >
                  Завершить заказ
                </button>
                <button
                  type="button"
                  disabled={busyId === d.id}
                  onClick={() => void resolveDispute(d, 'Cancelled')}
                  style={{ border: '1px solid var(--color-separator)', borderRadius: '999px', background: 'var(--color-surface)', color: '#FF3B30', padding: '10px 20px', fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', cursor: busyId === d.id ? 'default' : 'pointer', opacity: busyId === d.id ? 0.6 : 1 }}
                >
                  Отменить заказ
                </button>
              </div>
            </article>
            );
          })}

          {/* 423ee061: longest-open-first page of disputes */}
          <ListPager
            total={disputesTotal}
            offset={disputeOffset}
            limit={ADMIN_PAGE_SIZE}
            onPrev={() => setDisputeOffset(Math.max(0, disputeOffset - ADMIN_PAGE_SIZE))}
            onNext={() => setDisputeOffset(disputeOffset + ADMIN_PAGE_SIZE)}
          />
        </div>
      )}

      {tab === 'settings' && settings && (
        <>
        <form style={cardStyle} onSubmit={saveSettings}>
          <h3 style={{ fontSize: 'var(--fs-title)', fontWeight: 'var(--fw-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Платформа
          </h3>
          <label style={fieldStyle}>
            Минимальная сумма заказа, ₽
            <input
              type="number"
              min="0"
              max="1000000"
              value={settings.minOrderTotalRub}
              onChange={(e) => setSettings({ ...settings, minOrderTotalRub: Number(e.target.value) })}
              style={inputStyle}
              required
            />
          </label>
          <label style={fieldStyle}>
            Лимит активных товаров на исполнителя
            <input
              type="number"
              min="1"
              max="10000"
              value={settings.maxActiveProductsPerProvider}
              onChange={(e) => setSettings({ ...settings, maxActiveProductsPerProvider: Number(e.target.value) })}
              style={inputStyle}
              required
            />
          </label>
          <label style={{ ...fieldStyle, flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={settings.requireManualSellerApproval}
              onChange={(e) => setSettings({ ...settings, requireManualSellerApproval: e.target.checked })}
            />
            Ручное одобрение новых исполнителей
          </label>
          <label style={{ ...fieldStyle, flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
            />
            Режим обслуживания
          </label>
          {settings.maintenanceMode && (
            <label style={fieldStyle}>
              Текст объявления (до 500 символов)
              <textarea
                value={settings.maintenanceNotice}
                onChange={(e) => setSettings({ ...settings, maintenanceNotice: e.target.value })}
                maxLength={500}
                rows={3}
                style={inputStyle}
              />
            </label>
          )}
          {/* 1c936a07: global marketplace commission default — percent, blank
           * = follow the pricing-config default; orders snapshot the used
           * percent, so changes never reprice past orders. */}
          <label style={fieldStyle}>
            Комиссия маркетплейса, % (по умолчанию)
            <input
              type="text"
              inputMode="decimal"
              value={commissionText}
              onChange={(e) => setCommissionText(e.target.value)}
              placeholder="из конфига ценообразования"
              style={inputStyle}
            />
          </label>
          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: '-4px' }}>
            Диапазон (0; 50]. Пусто — значение из конфига ценообразования. Изменения не пересчитывают уже созданные заказы.
          </div>
          <button type="submit" style={{ alignSelf: 'flex-start', border: 'none', borderRadius: '999px', background: 'var(--color-primary)', color: '#fff', padding: '12px 24px', fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', cursor: 'pointer' }}>
            Сохранить настройки
          </button>
        </form>
        {/* 1c936a07: per-provider commission overrides — effective rate =
         * override ?? global default ?? config; future orders only. */}
        <section style={cardStyle}>
          <h3 style={{ fontSize: 'var(--fs-title)', fontWeight: 'var(--fw-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Комиссия по исполнителям
          </h3>
          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
            Переопределение для конкретного исполнителя (диапазон (0; 50]). Пусто — общий процент по умолчанию.
          </div>
          {providers.length === 0 && (
            <div style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>Нет профилей исполнителей</div>
          )}
          {providers.map((p) => {
            const draft = providerDrafts[p.userId] ?? commissionPercentToText(p.commissionRatePercent);
            return (
              <div key={p.userId} style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', borderBottom: '1px solid var(--color-separator)', paddingBottom: '10px' }}>
                <div style={{ minWidth: 220, flex: 1 }}>
                  <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)' }}>{p.companyName}</div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                    {p.email || '—'} · {commissionRowLabel(p.commissionRatePercent, settings?.marketplaceCommissionPercent ?? null, 15)}
                  </div>
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={draft}
                  placeholder="по умолчанию"
                  onChange={(e) => setProviderDrafts((prev) => ({ ...prev, [p.userId]: e.target.value }))}
                  style={{ ...inputStyle, width: 120 }}
                />
                <button
                  type="button"
                  disabled={savingProviderId === p.userId}
                  onClick={() => void saveProviderCommission(p, draft)}
                  style={{ border: 'none', borderRadius: '999px', background: 'var(--color-primary)', color: '#fff', padding: '10px 20px', fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', cursor: savingProviderId === p.userId ? 'default' : 'pointer', opacity: savingProviderId === p.userId ? 0.6 : 1 }}
                >
                  Сохранить
                </button>
                {p.commissionRatePercent != null && (
                  <button
                    type="button"
                    disabled={savingProviderId === p.userId}
                    onClick={() => void saveProviderCommission(p, '')}
                    style={{ border: '1px solid var(--color-separator)', borderRadius: '999px', background: 'var(--color-surface)', color: 'var(--color-text-primary)', padding: '10px 20px', fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', cursor: 'pointer' }}
                  >
                    Сбросить
                  </button>
                )}
              </div>
            );
          })}
        </section>
        <AdminPaymentsPanel
          settings={settings}
          onSave={savePaymentsSettings}
        />
        {/* 2f9c70ba: referral program hub — settings, ledger, attributions. */}
        <AdminReferralPanel />
        </>
      )}
    </section>
  );
}
